<template>
  <div class="container mt-5">
    <h2 class="text-center">Utah Hunt Login</h2>
    <form @submit.prevent="handleLogin" class="mt-4">
      <!-- Customer ID Field -->
      <div class="mb-3">
        <label for="customerId" class="form-label">Customer ID</label>
        <input
          type="text"
          class="form-control"
          id="customerId"
          v-model="customerId"
          required
        />
      </div>

      <!-- Date of Birth Field -->
      <div class="mb-3">
        <label for="dateOfBirth" class="form-label">Date of Birth (MM/DD/YYYY)</label>
        <input 
          type="text" 
          class="form-control" 
          id="dateOfBirth" 
          v-model="dateOfBirth"
          placeholder="MM/DD/YYYY"
          maxlength="10"
          @input="formatDateInput"
          @blur="validateDateOfBirth"
          required
        />
        <div v-if="dateOfBirthError" class="text-danger">
          {{ dateOfBirthError }}
        </div>
      </div>

      <!-- Display Method Selection -->
      <div class="mb-3">
        <label class="form-label d-block">Display Method</label>
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            name="displayMethod"
            id="redirectOption"
            value="redirect"
            v-model="displayMethod"
          />
          <label class="form-check-label" for="redirectOption">
            Redirect to Utah Hunt
          </label>
        </div>
        <div class="form-check form-check-inline">
          <input
            class="form-check-input"
            type="radio"
            name="displayMethod"
            id="iframeOption"
            value="iframe"
            v-model="displayMethod"
          />
          <label class="form-check-label" for="iframeOption">
            Display in iframe
          </label>
        </div>
      </div>

      <!-- Target Page Selection (Optional) -->
      <div class="mb-3">
        <label for="targetPage" class="form-label">Target Page (Optional)</label>
        <select class="form-select" id="targetPage" v-model="targetPage">
          <option value="">Default Page</option>
          <option value="Customer.aspx">Customer Profile</option>
          <option value="Applications.aspx">Applications</option>
          <option value="Bonus.aspx">Bonus Points</option>
        </select>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Buttons -->
      <button
        type="submit"
        class="btn btn-primary w-100"
        :disabled="isLoading || !isFormValid"
      >
        <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></span>
        <span v-else>Log into Utah Hunt</span>
      </button>
    </form>

    <!-- Iframe Container (shown only when iframe method is selected and login succeeded) -->
    <div v-if="displayMethod === 'iframe' && showIframe" class="mt-4">
      <div class="iframe-container">
        <iframe 
          :src="iframeUrl" 
          width="100%" 
          height="600px" 
          frameborder="0"
          title="Utah Hunt Portal"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UtahHuntLogin",
  data() {
    return {
      customerId: "",
      dateOfBirth: "",
      displayMethod: "redirect", // Default to redirect
      targetPage: "", // Default to empty (will use Customer.aspx)
      isLoading: false,
      error: null,
      dateOfBirthError: null,
      showIframe: false,
      iframeUrl: ""
    };
  },
  computed: {
    isFormValid() {
      return this.customerId && 
        this.dateOfBirth && 
        !this.dateOfBirthError;
    }
  },
  methods: {
    formatDateInput() {
      // Strip any non-numeric characters
      let input = this.dateOfBirth.replace(/\D/g, '');
      
      // Add slashes automatically
      if (input.length > 2) {
        input = input.slice(0, 2) + '/' + input.slice(2);
      }
      if (input.length > 5) {
        input = input.slice(0, 5) + '/' + input.slice(5, 9);
      }
      
      // Update the form value (limits to 10 characters: MM/DD/YYYY)
      this.dateOfBirth = input.substring(0, 10);
      
      // Clear error when typing
      if (this.dateOfBirthError) {
        this.dateOfBirthError = null;
      }
    },
    
    validateDateOfBirth() {
      // Reset error
      this.dateOfBirthError = null;
      
      // Skip validation if empty
      if (!this.dateOfBirth) {
        return;
      }
      
      // Check format (MM/DD/YYYY)
      const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
      if (!dateRegex.test(this.dateOfBirth)) {
        this.dateOfBirthError = "Please enter a valid date in MM/DD/YYYY format";
        return false;
      }
      
      // Check if date is valid
      const [month, day, year] = this.dateOfBirth.split('/').map(Number);
      const date = new Date(year, month - 1, day);
      
      // Verify the date is valid (if month or day are invalid, the date object will adjust accordingly)
      if (date.getMonth() !== month - 1 || date.getDate() !== day) {
        this.dateOfBirthError = "Please enter a valid date";
        return false;
      }
      
      return true;
    },

    async handleLogin() {
      if (!this.validateDateOfBirth()) {
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.showIframe = false;

      try {
        if (this.displayMethod === 'redirect') {
          this.handleRedirect();
        } else if (this.displayMethod === 'iframe') {
          await this.handleIframeLogin();
        }
      } catch (err) {
        this.error = "An error occurred while trying to log in. Please try again.";
        console.error("Login error:", err);
      } finally {
        this.isLoading = false;
      }
    },

    handleRedirect() {
      // Construct the redirect URL
      let redirectUrl = `${this.$API_BASE_URL}/api/utah-hunt/redirect?customerId=${encodeURIComponent(this.customerId)}&dateOfBirth=${encodeURIComponent(this.dateOfBirth)}`;
      
      if (this.targetPage) {
        redirectUrl += `&targetPage=${encodeURIComponent(this.targetPage)}`;
      }
      
      // Redirect to the Utah Hunt website
      window.location.href = redirectUrl;
    },

    async handleIframeLogin() {
      try {
        // Parse the date from MM/DD/YYYY format to ISO format (YYYY-MM-DD)
        const [month, day, year] = this.dateOfBirth.split('/').map(Number);
        const formattedDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        // First authenticate with the login API
        const response = await fetch(`${this.$API_BASE_URL}/api/utah-hunt/login`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            customerId: this.customerId,
            dateOfBirth: formattedDate // Send date in ISO format (YYYY-MM-DD)
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to log in to Utah Hunt website");
        }

        // If login successful, set up the iframe URL
        let iframeUrl = `${this.$API_BASE_URL}/api/utah-hunt/iframe?customerId=${encodeURIComponent(this.customerId)}&dateOfBirth=${encodeURIComponent(this.dateOfBirth)}`;
        
        if (this.targetPage) {
          iframeUrl += `&targetPage=${encodeURIComponent(this.targetPage)}`;
        }
        
        this.iframeUrl = iframeUrl;
        this.showIframe = true;
      } catch (error) {
        this.error = error.message || "An error occurred while trying to log in";
        this.showIframe = false;
      }
    }
  }
};
</script>

<style scoped>
.iframe-container {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.form-check-inline {
  margin-right: 1rem;
}

.alert {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.spinner-border {
  vertical-align: middle;
}
</style>
